import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import {
  MessageSquarePlus,
  ShoppingCart,
  ClipboardList,
  ClipboardCheck,
  BarChart3,
  LineChart,
  Package,
  LayoutDashboard,
  Activity,
  Home,
  SlidersHorizontal,
  ShieldCheck,
  Route,
  ArrowLeftRight,
  Shield,
  Gauge,
  Bookmark,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  FolderOpen,
  History,
  Settings,
  User,
  MoreVertical,
  Trash2,
  LogOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnalysis } from '@/contexts/AnalysisContext';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { MAX_FOLDERS_IN_SIDEBAR } from './CategoryFoldersView';

const SIDEBAR_WIDTH = '260px';
const SIDEBAR_COLLAPSED_WIDTH = '72px';

interface SidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onCollapsedChange }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isModuleVisible, isActionEnabled } = useFeatureVisibility();
  const { data, getFoldersByCategoryId, deleteFolder } = useAnalysis();
  const displayName = user?.name ?? user?.username ?? t('common.profile');
  const [openCategoryIds, setOpenCategoryIds] = useState<Set<string>>(() => new Set(data.categories.map((c) => c.id)));
  const [openFolderMenuId, setOpenFolderMenuId] = useState<string | null>(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileMenuAnchorRect, setProfileMenuAnchorRect] = useState<DOMRect | null>(null);
  
  // Verificar feature flag para AI Chat
  const isAIChatEnabled = 
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' || 
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  const showSupplyRulesNav =
    isModuleVisible('supply', true) &&
    (isActionEnabled('supply.agent_simulate', false) ||
      import.meta.env.VITE_SUPPLY_AGENT_ENABLED === 'true');

  // Cuando se añaden nuevas categorías
  useEffect(() => {
    const categoryIds = new Set(data.categories.map((c) => c.id));
    setOpenCategoryIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      categoryIds.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [data.categories]);

  const setCollapsed = (fn: (c: boolean) => boolean) => onCollapsedChange(fn(collapsed));

  const toggleCategory = (id: string) => {
    setOpenCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const navLink = (to: string, label: string, icon: React.ReactNode, end?: boolean) => (
    <NavLink
      to={to}
      end={end ?? (to === '/' || to === '/home' || to === 'supply')}
      className={({ isActive }) =>
        `w-full flex items-center rounded-lg transition-colors ${
          collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5 text-left'
        } ${isActive ? 'bg-gray-200/80 text-gray-800' : 'text-gray-700 hover:bg-gray-100 hover:text-stocker-primary'}`
      }
    >
      {icon}
      {!collapsed && <span className="text-sm font-medium">{label}</span>}
    </NavLink>
  );

  const moduleIconClass = 'w-5 h-5 text-gray-600 flex-shrink-0';

  /** Planificación: Previsión Demanda (legacy en /forecast) + S&OP V2 (opcional). */
  const renderPlanningModules = () => (
    <>
      {isModuleVisible('forecast', true) &&
        navLink(
          'forecast',
          t('sidebar.demandForecast'),
          <LineChart className={moduleIconClass} />,
        )}
      {isModuleVisible('demand_planning', true) ? (
        <NavLink
          to="demand-planning/sop/plan"
          className={() => {
            const active = location.pathname.startsWith('/demand-planning');
            return `w-full flex items-center rounded-lg transition-colors ${
              collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5 text-left'
            } ${active ? 'bg-gray-200/80 text-gray-800' : 'text-gray-700 hover:bg-gray-100 hover:text-stocker-primary'}`;
          }}
        >
          <BarChart3 className={moduleIconClass} />
          {!collapsed && <span className="text-sm font-medium">{t('sidebar.sop')}</span>}
        </NavLink>
      ) : null}
    </>
  );

  const openFolder = openFolderMenuId ? data.folders.find((f) => f.id === openFolderMenuId) : null;
  const closeFolderMenu = () => {
    setOpenFolderMenuId(null);
    setMenuAnchorRect(null);
  };
  const closeProfileMenu = () => {
    setProfileMenuOpen(false);
    setProfileMenuAnchorRect(null);
  };
  const handleLogout = () => {
    closeProfileMenu();
    logout();
    navigate('/login');
  };

  return (
    <>
      {openFolderMenuId && menuAnchorRect && openFolder &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={closeFolderMenu}
              aria-hidden
            />
            <div
              className="fixed z-[101] py-1 rounded-lg bg-white border border-gray-200 shadow-lg min-w-[160px]"
              style={{
                top: menuAnchorRect.bottom + 4,
                left: Math.min(menuAnchorRect.left, typeof window !== 'undefined' ? window.innerWidth - 180 : menuAnchorRect.left),
              }}
            >
              <button
                type="button"
                onClick={() => {
                  deleteFolder(openFolder.id);
                  closeFolderMenu();
                  if (location.pathname === `/analysis/folder/${openFolder.id}`) {
                    navigate('/');
                  }
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t('sidebar.deleteFolder')}
              </button>
            </div>
          </>,
          document.body
        )}
      {profileMenuOpen && profileMenuAnchorRect &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={closeProfileMenu}
              aria-hidden
            />
            <div
              className="fixed z-[101] py-1 rounded-lg bg-white border border-gray-200 shadow-lg min-w-[160px]"
              style={{
                bottom: window.innerHeight - profileMenuAnchorRect.top + 4,
                left: Math.min(profileMenuAnchorRect.left, window.innerWidth - 180),
              }}
            >
              <button
                type="button"
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                {t('common.logout')}
              </button>
            </div>
          </>,
          document.body
        )}
      <aside
      className="flex flex-col h-full bg-stocker-sidebar border-r border-gray-200 transition-[width] duration-200 ease-in-out relative"
      style={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        minWidth: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
      }}
    >
      <div className={`p-3 flex-shrink-0 ${collapsed ? 'px-2' : ''}`}>
        <div
          className={`flex items-center rounded-lg bg-[#1681ba] text-white ${
            collapsed ? 'justify-center p-2' : 'gap-2 px-2 py-2'
          }`}
        >
          <FontAwesomeIcon
            icon={faWarehouse}
            className="h-8 w-8 flex-shrink-0"
            style={{ transform: 'rotate(-30deg)' }}
          />
          {!collapsed && (
            <h1 className="text-xl font-bold whitespace-nowrap">Stocker <sup>AI</sup></h1>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="absolute -right-3 top-6 z-10 w-6 h-6 rounded-full bg-stocker-sidebar border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:bg-stocker-primaryLight hover:text-stocker-primary hover:border-stocker-primary/30 transition-colors"
        aria-label={collapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
      >
        {collapsed ? (
          <ChevronRight className="w-3.5 h-3.5" />
        ) : (
          <ChevronLeft className="w-3.5 h-3.5" />
        )}
      </button>

      <div className={`pt-4 pb-2 flex-shrink-0 ${collapsed ? 'px-2' : 'px-3'}`}>
        {navLink('/home', t('sidebar.home'), <Home className="w-5 h-5 flex-shrink-0" />)}
        {isModuleVisible('usage_monitoring', false) &&
          navLink(
            'usage-monitoring',
            t('navigation.adoption'),
            <Gauge className="w-5 h-5 flex-shrink-0" />,
          )}
      </div>

      {isAIChatEnabled && (
        <div className={`pb-2 flex-shrink-0 ${collapsed ? 'px-2' : 'px-3'}`}>
          {navLink('/', t('sidebar.newChat'), <MessageSquarePlus className="w-5 h-5 flex-shrink-0" />)}
        </div>
      )}

      <nav className={`flex-1 overflow-y-auto space-y-0.5 ${collapsed ? 'px-2' : 'px-3'}`}>
        {!collapsed && (
          <div className="pb-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-1.5">
              {t('sidebar.planning')}
            </div>
            {isModuleVisible('compliance_monitor', true) &&
              navLink('compliance-monitor', t('navigation.complianceMonitor'), <ShieldCheck className={moduleIconClass} />)}
            {renderPlanningModules()}
          </div>
        )}
        {!collapsed && (
          <div className="pb-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-1.5">
              {t('sidebar.abastecimiento')}
            </div>
            {isModuleVisible('inventory_policies', true) &&
              navLink('inventory-policies', t('navigation.inventoryPolicies'), <Package className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('inventory_simulation', false) &&
              navLink('inventory-simulation', t('navigation.inventorySimulation'), <Activity className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('network_replenishment', false) &&
              navLink('network-replenishment', t('navigation.networkReplenishment'), <ArrowLeftRight className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('safety_stock', false) &&
              navLink('safety-stock', t('navigation.safetyStock'), <Shield className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('inventory_policy_control', false) &&
              navLink('inventory-policy-control', t('navigation.inventoryPolicyControl'), <LayoutDashboard className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('business_rules', true) &&
              navLink('business-rules', t('navigation.businessRules'), <SlidersHorizontal className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
          </div>
        )}
        {!collapsed && (
          <div className="pb-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-1.5">
              {t('sidebar.purchasing')}
            </div>
            {isModuleVisible('supply', true) && navLink('supply', t('navigation.supply'), <ShoppingCart className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {showSupplyRulesNav &&
              navLink(
                '/supply/rules',
                t('navigation.supplyRules'),
                <Bookmark className="w-5 h-5 text-gray-600 flex-shrink-0" />,
                true,
              )}
            {isModuleVisible('order_tracking', true) &&
              navLink('order-tracking', t('navigation.orderTracking'), <Route className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('orders', true) && navLink('orders', t('navigation.orders'), <ClipboardList className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('customer_fulfillment', true) &&
              navLink('customer-fulfillment', t('navigation.customerFulfillment'), <ClipboardCheck className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
          </div>
        )}
        {collapsed && (
          <>
            {isModuleVisible('compliance_monitor', true) &&
              navLink('compliance-monitor', t('navigation.complianceMonitor'), <ShieldCheck className={moduleIconClass} />)}
            {renderPlanningModules()}
            {isModuleVisible('inventory_policies', true) &&
              navLink('inventory-policies', t('navigation.inventoryPolicies'), <Package className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('inventory_simulation', false) &&
              navLink('inventory-simulation', t('navigation.inventorySimulation'), <Activity className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('network_replenishment', false) &&
              navLink('network-replenishment', t('navigation.networkReplenishment'), <ArrowLeftRight className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('safety_stock', false) &&
              navLink('safety-stock', t('navigation.safetyStock'), <Shield className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('inventory_policy_control', false) &&
              navLink('inventory-policy-control', t('navigation.inventoryPolicyControl'), <LayoutDashboard className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('business_rules', true) &&
              navLink('business-rules', t('navigation.businessRules'), <SlidersHorizontal className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('supply', true) && navLink('supply', t('navigation.supply'), <ShoppingCart className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {showSupplyRulesNav &&
              navLink(
                '/supply/rules',
                t('navigation.supplyRules'),
                <Bookmark className="w-5 h-5 text-gray-600 flex-shrink-0" />,
                true,
              )}
            {isModuleVisible('order_tracking', true) &&
              navLink('order-tracking', t('navigation.orderTracking'), <Route className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('orders', true) && navLink('orders', t('navigation.orders'), <ClipboardList className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
            {isModuleVisible('customer_fulfillment', true) &&
              navLink('customer-fulfillment', t('navigation.customerFulfillment'), <ClipboardCheck className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
          </>
        )}

        {isAIChatEnabled && !collapsed && (
          <div className="pt-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-1.5">
              {t('sidebar.analysis')}
            </div>
            {data.categories.map((category) => {
              const isOpen = openCategoryIds.has(category.id);
              const folders = getFoldersByCategoryId(category.id);
              return (
                <div key={category.id}>
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-stocker-primaryLight hover:text-stocker-primary transition-colors"
                  >
                    <span className="text-sm font-medium">{category.name}</span>
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="pl-4 mt-0.5 space-y-0.5">
                      {folders.slice(0, MAX_FOLDERS_IN_SIDEBAR).map((folder) => (
                        <div
                          key={folder.id}
                          className="flex items-center gap-0.5 rounded-lg group/folder min-w-0"
                        >
                          <NavLink
                            to={`/analysis/folder/${folder.id}`}
                            className={({ isActive }) =>
                              `flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                                isActive
                                  ? 'bg-stocker-primaryLight text-stocker-primary'
                                  : 'text-gray-600 hover:bg-stocker-primaryLight hover:text-stocker-primary'
                              }`
                            }
                          >
                            <FolderOpen className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{folder.name}</span>
                          </NavLink>
                          <div className="relative flex-shrink-0 pr-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                const next = openFolderMenuId === folder.id ? null : folder.id;
                                setOpenFolderMenuId(next);
                                setMenuAnchorRect(next ? (e.currentTarget as HTMLButtonElement).getBoundingClientRect() : null);
                              }}
                              className="p-1.5 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                              aria-label={t('sidebar.folderOptions')}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {folders.length > MAX_FOLDERS_IN_SIDEBAR && (
                        <Link
                          to={`/analysis/category/${category.id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm text-gray-500 hover:bg-stocker-primaryLight hover:text-stocker-primary transition-colors"
                        >
                          <span className="truncate">...</span>
                          <span className="text-xs">{t('sidebar.viewAll')}</span>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {isAIChatEnabled && collapsed && (
          <button
            type="button"
            className="w-full flex justify-center py-2.5 rounded-lg text-gray-700 hover:bg-stocker-primaryLight hover:text-stocker-primary transition-colors"
            title={t('sidebar.analysis')}
          >
            <FolderOpen className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </nav>

      <div className={`pt-0 border-t border-gray-200 flex-shrink-0 space-y-0.5 ${collapsed ? 'p-2' : 'p-3'}`}>
        {isAIChatEnabled && isModuleVisible('history', true) && navLink('history', t('app.historyTitle'), <History className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
        {isModuleVisible('settings', true) && navLink('settings', t('navigation.settings'), <Settings className="w-5 h-5 text-gray-600 flex-shrink-0" />)}
        <button
          type="button"
          onClick={(e) => {
            setProfileMenuOpen((open) => !open);
            setProfileMenuAnchorRect((e.currentTarget as HTMLButtonElement).getBoundingClientRect());
          }}
          className={`w-full flex items-center rounded-lg text-gray-700 hover:bg-stocker-primaryLight hover:text-stocker-primary transition-colors ${
            collapsed ? 'justify-center py-2.5' : 'gap-3 px-3 py-2.5'
          }`}
          title={collapsed ? displayName : undefined}
        >
          <div className="w-8 h-8 rounded-full bg-stocker-primary/20 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-stocker-primary" />
          </div>
          {!collapsed && <span className="text-sm font-medium truncate">{displayName}</span>}
        </button>
      </div>
    </aside>
    </>
  );
}
